<template>
  <Modal
    :show="show"
    :title="isEditing ? $t('workspace.modal.editTask') : $t('workspace.modal.createTask')"
    size="large"
    :close-on-backdrop-click="!submitting"
    :loading="submitting"
    no-padding
    @close="closeModal"
  >
    <form @submit.prevent="handleSubmit" class="task-form">
        <!-- Task Name -->
        <div class="form-group">
          <label for="taskName" class="required">{{ $t('workspace.modal.taskName') }}</label>
          <input
            id="taskName"
            v-model="form.name"
            type="text"
            :placeholder="$t('workspace.modal.taskNamePlaceholder')"
            required
            minlength="3"
            maxlength="100"
          />
          <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
        </div>

        <!-- Description -->
        <div class="form-group">
          <label for="taskDescription">{{ $t('workspace.modal.description') }}</label>
          <textarea
            id="taskDescription"
            v-model="form.description"
            :placeholder="$t('workspace.modal.descriptionPlaceholder')"
            maxlength="500"
            rows="3"
          ></textarea>
        </div>

        <!-- Amount (Currency Input) -->
        <div class="form-group">
          <label for="taskAmount">{{ $t('workspace.modal.amount') }}</label>
          <div class="currency-input">
            <span class="currency-symbol">{{ userCurrency }}</span>
            <input
              id="taskAmount"
              v-model.number="form.amount"
              type="number"
              min="0"
              step="0.01"
              :placeholder="$t('workspace.modal.amountPlaceholder')"
            />
          </div>
          <span v-if="errors.amount" class="error-message">{{ errors.amount }}</span>
        </div>

        <!-- Phase Dropdown -->
        <div v-if="project?.multiPhase" class="form-group">
          <label for="taskPhase">{{ $t('workspace.modal.phase') }}</label>
          <select id="taskPhase" v-model="form.phaseId">
            <option :value="null">{{ $t('workspace.modal.selectPhase') }}</option>
            <option v-for="phase in phases" :key="phase.id" :value="phase.id">
              {{ phase.name }}
            </option>
          </select>
        </div>

        <!-- Intervenant Dropdown -->
        <div class="form-group">
          <label for="taskIntervenant">{{ $t('workspace.modal.intervenant') }}</label>
          <select id="taskIntervenant" v-model="form.intervenantId">
            <option :value="null">{{ $t('workspace.modal.selectIntervenant') }}</option>
            <option v-for="intervenant in intervenants" :key="intervenant.id" :value="intervenant.id">
              {{ intervenant.name }}
            </option>
          </select>
        </div>

        <!-- Date Range -->
        <div class="form-row">
          <div class="form-group">
            <label for="taskStartDate">{{ $t('workspace.modal.startDate') }}</label>
            <input
              id="taskStartDate"
              v-model="form.startDate"
              type="date"
            />
          </div>
          <div class="form-group">
            <label for="taskEndDate">{{ $t('workspace.modal.endDate') }}</label>
            <input
              id="taskEndDate"
              v-model="form.endDate"
              type="date"
              :min="form.startDate"
            />
          </div>
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            {{ $t('common.cancel') }}
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            <span v-if="submitting" class="spinner"></span>
            {{ submitting ? $t('common.saving') : $t('common.save') }}
          </button>
        </div>
      </form>
    </Modal>
  </template>

<script setup lang="ts">
// @ts-ignore
import { ref as vueRef, computed as vueComputed, watch as vueWatch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import { getUserCurrency } from '@/utils/currencyUtils'
import Modal from '@/components/base/Modal.vue'
import projectTasksService from '@/services/projectTasks.service'

// Aliases to avoid TypeScript errors
const ref = vueRef
const computed = vueComputed
const watch = vueWatch

interface Task {
  id?: number
  projectId: number
  name: string
  description?: string
  amount: number
  intervenantName?: string
  intervenantId?: number | null
  phaseId?: number | null
  phaseName?: string
  startDate?: string
  endDate?: string
}

interface Project {
  id: number
  multiPhase?: boolean
}

interface Phase {
  id: number
  name: string
}

interface Intervenant {
  id: number
  name: string
}

const props = defineProps<{
  show: boolean
  task: Task | null
  projectId: number
  project: Project | null
  phases: Phase[]
  intervenants: Intervenant[]
}>()

const emit = defineEmits<{
  close: []
  saved: []
}>()

const { t } = useI18n()
const { notifySuccess, notifyError } = useNotifications()

// Form state
const form = ref<Task>({
  projectId: props.projectId,
  name: '',
  description: '',
  amount: 0,
  intervenantId: null,
  phaseId: null,
  startDate: '',
  endDate: ''
})

const errors = ref<Record<string, string>>({})
const submitting = ref(false)

// Computed
const isEditing = computed(() => !!props.task?.id)
const userCurrency = computed(() => getUserCurrency())

// Methods
const validateForm = (): boolean => {
  errors.value = {}
  
  if (!form.value.name || form.value.name.trim().length < 3) {
    errors.value.name = t('workspace.modal.errors.nameRequired')
    return false
  }
  
  if (form.value.amount < 0) {
    errors.value.amount = t('workspace.modal.errors.invalidAmount')
    return false
  }
  
  if (form.value.startDate && form.value.endDate) {
    if (new Date(form.value.endDate) < new Date(form.value.startDate)) {
      errors.value.endDate = t('workspace.modal.errors.endDateBeforeStart')
      return false
    }
  }
  
  return true
}

const handleSubmit = async () => {
  if (!validateForm()) return
  
  submitting.value = true
  
  try {
    const taskData = {
      ...form.value,
      projectId: props.projectId
    }
    
    if (isEditing.value && form.value.id) {
      // Update existing task
      await projectTasksService.updateTask(
        props.projectId.toString(),
        form.value.id.toString(),
        taskData
      )
      notifySuccess(t('workspace.success.taskUpdated'))
    } else {
      // Create new task
      await projectTasksService.createTask(
        props.projectId.toString(),
        taskData
      )
      notifySuccess(t('workspace.success.taskCreated'))
    }
    
    emit('saved')
    closeModal()
  } catch (error) {
    notifyError(t('workspace.error.taskSaveFailed'))
  } finally {
    submitting.value = false
  }
}

const closeModal = () => {
  form.value = {
    projectId: props.projectId,
    name: '',
    description: '',
    amount: 0,
    intervenantId: null,
    phaseId: null,
    startDate: '',
    endDate: ''
  }
  errors.value = {}
  emit('close')
}

// Watch for task changes (editing mode)
watch(() => props.task, (newTask: Task | null) => {
  if (newTask) {
    form.value = {
      ...newTask,
      projectId: props.projectId
    }
  } else {
    form.value = {
      projectId: props.projectId,
      name: '',
      description: '',
      amount: 0,
      intervenantId: null,
      phaseId: null,
      startDate: '',
      endDate: ''
    }
  }
}, { immediate: true })
</script>

<style scoped>
/* Form Container */
.task-form {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.form-group {
  margin-bottom: 1.5rem;
  padding: 0 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--neutral-700);
}

.form-group label.required::after {
  content: ' *';
  color: var(--error-600);
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.15s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 0 1.5rem;
  margin-bottom: 1.5rem;
}

.form-row .form-group {
  margin-bottom: 0;
  padding: 0;
}

.currency-input {
  position: relative;
  display: flex;
  align-items: center;
}

.currency-symbol {
  position: absolute;
  left: 0.75rem;
  color: var(--neutral-600);
  font-weight: 600;
  font-size: 0.875rem;
  pointer-events: none;
}

.currency-input input {
  padding-left: 3.5rem;
}

.error-message {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--error-600);
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid var(--neutral-200);
  background: var(--neutral-50);
  margin-top: auto;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: none;
  font-size: 0.875rem;
}

.btn-secondary {
  background: var(--neutral-100);
  color: var(--neutral-700);
}

.btn-secondary:hover {
  background: var(--neutral-200);
}

.btn-primary {
  background: var(--primary-600);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-700);
}

.btn-primary:disabled {
  background: var(--neutral-300);
  color: var(--neutral-500);
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
